import React from 'react';
import { Cloud, Crown, HardDrive, Key, Monitor, Plug, Server, Sun, Twitter, User } from 'lucide-react';

/** The sections of Settings; "plan" is the plan page, which shares this navigation. */
export type SettingsNavId = 'account' | 'devices' | 'plan' | 'api' | 'twitter' | 'claude' | 'autosave' | 'appearance' | 'comfyui' | 'status' | 'admin';

interface SettingsNavProps {
    active: SettingsNavId | null;
    onSelect: (id: SettingsNavId) => void;
    signedIn: boolean;
    isAdmin?: boolean;
    isPro?: boolean;
    twitterConnected?: boolean;
    /** How many API keys are saved: shown beside the item */
    keyCount?: number;
}

interface Item { id: SettingsNavId; label: string; icon: React.ReactNode; trail?: React.ReactNode }

/**
 * The left navigation of Settings, in four groups: the account (profile, devices, the plan),
 * the keys, what the app connects to, and how the app behaves. Admins get a fifth. The same
 * component sits beside the plan page, so plan, credits and usage live with the account.
 */
export const SettingsNav: React.FC<SettingsNavProps> = ({ active, onSelect, signedIn, isAdmin = false, isPro = false, twitterConnected = false, keyCount = 0 }) => {
    const groups: { label: string; items: Item[] }[] = [
        ...(signedIn ? [{
            label: 'Account',
            items: [
                { id: 'account' as const, label: 'Profile', icon: <User /> },
                { id: 'devices' as const, label: 'Devices', icon: <Monitor /> },
                { id: 'plan' as const, label: 'Plan and credits', icon: <Crown />, trail: isPro ? <span className="ns-badge ns-badge-ok">Pro</span> : undefined },
            ],
        }] : []),
        { label: 'Keys', items: [{ id: 'api', label: 'API keys', icon: <Key />, trail: keyCount > 0 ? <span className="ns-t-meta tabular-nums">{keyCount}</span> : undefined }] },
        {
            label: 'Connections',
            items: [
                { id: 'twitter', label: 'X (Twitter)', icon: <Twitter />, trail: twitterConnected ? <span className="ns-tone-dot" data-tone="ok" aria-label="Connected" /> : undefined },
                ...(signedIn ? [{ id: 'claude' as const, label: 'Claude', icon: <Plug /> }] : []),
                { id: 'autosave', label: 'Local folder', icon: <HardDrive /> },
            ],
        },
        {
            label: 'The app',
            items: [
                { id: 'appearance', label: 'Appearance', icon: <Sun /> },
                { id: 'comfyui', label: 'Local models', icon: <Server /> },
                { id: 'status', label: 'Status', icon: <Cloud /> },
            ],
        },
        ...(signedIn && isAdmin ? [{ label: 'Admin', items: [{ id: 'admin' as const, label: 'Pro grants', icon: <Crown /> }] }] : []),
    ];

    return (
        <nav className="ns-glass ns-glass-strong ns-lib-side rounded-2xl flex flex-col gap-3" aria-label="Settings sections">
            {groups.map(group => (
                <div key={group.label}>
                    <p className="ns-t-label px-2 pb-1">{group.label}</p>
                    <div className="flex flex-col gap-0.5">
                        {group.items.map(item => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => onSelect(item.id)}
                                aria-current={active === item.id ? 'page' : undefined}
                                className="ns-row [&>svg]:w-4 [&>svg]:h-4"
                            >
                                {item.icon}
                                <span className="flex-1 min-w-0 truncate text-left">{item.label}</span>
                                {item.trail}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </nav>
    );
};
